import React from 'react'
import {connect} from 'react-redux'
import cx from 'classnames'
import {ReportEditor} from './ReportEditor'
import {MastheadEditor} from './MastheadEditor'
import {Header} from './Header'
import styles from './EditPage.scss'
import appStyles from '../App/appStyles.scss'
import {convertToRaw, KeyBindingUtil, convertFromRaw, EditorState, ContentState} from 'draft-js'
import * as reportActions from 'lib/api/rideReports'
import {CircleSpinner} from 'common/spinners'
import {Navbar, NavbarGroup, NavbarTitle} from 'common/Navbar'
import _ from 'lodash'
import { browserHistory } from 'react-router'
import { generateReportBlocks } from 'lib/insights'
import {OnboardingContainer} from './Onboarding'
import {keyIn} from 'lib/utils'
import * as tripActions from 'lib/api/trips'
import * as userActions from 'lib/api/currentUser'
import {RideRepicker} from './RideRepicker'
import Immutable, {List, Map} from 'immutable'
import {Button} from 'common/Button'
import {getConsole} from 'lib/getConsole'
import Helmet from 'react-helmet'
import fetchJSON from 'lib/fetchJSON'
import {MaxReachedDialog} from './MaxReachedDialog'
import {SaveFailureDialog} from './SaveFailureDialog'
import {Icon} from 'common/LegacyIcon'
import {editorStateFromDefault} from 'lib/draftUtils'
import {showModal} from 'lib/modalUtils'
import {FORCE_SAVE_RR_MODAL} from 'common/modals/ForceSaveRRModal'

const {hasCommandModifier} = KeyBindingUtil
const console = getConsole('rr', 'EditPage')

export class EditPage extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      editorState: null,
      showRidePicker: false,
      showMaxReachedDialog: false,
      saveFailureDialogDismissed: false
    }
    // special case, we do this on constructor to prevent flicker on initial mount
    if (props.report) {
      this.state.editorState = this.initEditorState()
    }
    this.autosave = _.throttle(this.safeRequestSave, 1500, {leading: false})
  }

  safeRequestSave = (report) => {
    this.props.requestSave(report)
      .catch(e => {
        if (e.errors[0].error_info === 'updated_at_desync') {
          showModal(FORCE_SAVE_RR_MODAL, {
            loadLatest: () => this.props.loadReport(report.id),
            reinitialize: () => this.onReportLoaded(true),
            overwrite: () => this.props.requestSave(report, {force: true})
          })
        } else {
          throw e
        }
      })
  }

  componentDidMount () {
    const {report, loadReport, reportId} = this.props

    if (report) {
      this.onReportLoaded()
    } else {
      loadReport(reportId).then(this.onReportLoaded)
    }
  }

  onReportLoaded = (force = false) => {
    if (!this.state.editorState || force) { this.setState({editorState: this.initEditorState()}) }
    this.ensureRidesLoaded()
    this.checkForUpsell()
  }

  initEditorState = () => {
    const {report} = this.props
    const reportContent = report.reportContent
    const editorState = reportContent
      ? editorStateFromDefault(convertFromRaw(reportContent))
      : null
    this._lastContent = editorState && editorState.getCurrentContent()
    return editorState
  }

  ensureRidesLoaded = (rideIds) => {
    const {rides, loadRide, report} = this.props
    if (!report) { return }
    // this.props.updateReport({...report, rides: [19048759]})
    // setTimeout(this.props.requestSave(report).then(() => window.location.reload()), 2000)
    (rideIds || report.rides).forEach(id => {
      if (!rides.get(id)) { loadRide(id) }
    })
  }

  // concurrency-aware upsell check (works if user performs actions in another tab)
  checkForUpsell = () => {
    const {report} = this.props

    // don't bother the user if they're editing a published report
    if (report.publishStatus !== 'draft') {
      return
    }

    return fetchJSON('/ride_reports/can_publish.json')
      .then((res) => {
        if (!res.can_publish) {
          this.setState({showMaxReachedDialog: true})
          return false
        } else {
          return true
        }
      })
      .catch(() => {
        // TODO error handling
      })
  }

  dismissMaxReachedDialog = () => this.setState({showMaxReachedDialog: false})

  dismissSaveFailureDialog = () => this.setState({saveFailureDialogDismissed: true})

  // problem: blocks are steamrolling onKeydown
  onKeyDown = (e) => {
    const {report} = this.props
    const S = 83
    if (e.keyCode === S && hasCommandModifier(e)) {
      e.preventDefault()
      this.safeRequestSave(report)
    }
  }

  onReportContentChanged = (editorState, cb) => {
    const {updateReport, report} = this.props
    this.setState({editorState}, cb)

    const prevBlockMap = this._lastContent && this._lastContent.getBlockMap()
    if (prevBlockMap !== editorState.getCurrentContent().getBlockMap()) {
      const updated = {...report, reportContent: convertToRaw(editorState.getCurrentContent())}
      updateReport(updated)
      this.autosave(updated)
      this._lastContent = editorState.getCurrentContent()
    }
  }

  onTitleChange = (titleOrSubtitle) => {
    const {updateReport, report} = this.props
    const updated = {...report, ...titleOrSubtitle}
    updateReport(updated)
    this.autosave(updated)
  }

  toggleMetric = () => {
    const {updateReport, report} = this.props
    const updated = {...report, metricUnits: !report.metricUnits}
    updateReport(updated)
    this.autosave(updated)
  }

  onStatusChange = (publishStatus, contestEntry = false) => {
    const {updateReport, report} = this.props

    const doPublish = () => {
      const newReport = {...report, publishStatus, contestEntry}
      updateReport(newReport)
      return this.safeRequestSave(newReport)
    }

    if (report.publishStatus === 'draft' && publishStatus !== 'draft') {
      // trying to publish a draft
      return this.checkForUpsell().then((canPublish) => {
        if (canPublish) {
          return doPublish()
        }
      })
    }

    return doPublish()
  }

  onRidesChange = (rides) => {
    const {updateReport, report} = this.props
    const rideIds = rides.keySeq().toJS()

    if (!report.rides.every(id => rideIds.indexOf(id) !== -1)) {
      // rides have been removed from the report, filtering report
      // content
      const {editorState} = this.state
      const isInReport = (id) => rides.has(id)
      const contentState = editorState.getCurrentContent()
      const blocks = contentState
        .getBlockMap()
        .filter(b => {
          const idRef = b.getData().get('rideId', false)
          return idRef ? isInReport(idRef) : true
        })
      // it's inefficient and also a massive pain to model this filter operation
      // as a sequence of chained removeRange calls, so instead we operate
      // directly on the blockMap and re-inflate the editorState from it.
      //
      // this also has the (desireable in this case) side effect of wiping out
      // undo/redo history, which could otherwise allow the user to recover a
      // mapBlock pointing to a removed ride.

      const filteredContentState = contentState.merge({blockMap: blocks})
      const rawContentState = convertToRaw(filteredContentState)
      const reprocessed = editorStateFromDefault(convertFromRaw(rawContentState))
      const newReport = {...report, reportContent: rawContentState, rides: rideIds}
      this.setState({showRidePicker: false, editorState: reprocessed})
      updateReport(newReport)
      this.safeRequestSave(newReport)
      this.ensureRidesLoaded(rideIds)
    } else {
      const newReport = {...report, rides: rideIds}
      this.setState({showRidePicker: false})
      updateReport(newReport)
      this.safeRequestSave(newReport)
      this.ensureRidesLoaded(rideIds)
    }
  }

  confirmDelete = () => {
    // for now, deletion is permanent. Probably a good idea to implement a 'recycle bin' here instead.
    const {deleteReport, report} = this.props
    deleteReport(report)
      .then(res =>
        browserHistory.push('/ride_reports/drafts')
      )
  }

  reportContentUndo = () => {
    const {editorState} = this.state
    const newEditorState = EditorState.undo(editorState)
    this.onReportContentChanged(newEditorState)
  }

  reportContentRedo = () => {
    const {editorState} = this.state
    const newEditorState = EditorState.redo(editorState)
    this.onReportContentChanged(newEditorState)
  }

  autoPopulate = () => {
    const {rides, report} = this.props
    const {editorState} = this.state
    if (editorState) {
      console.warn('autoPopulate called with non-empty editorState')
    }

    const blocks = generateReportBlocks(report, rides)
    const contentState = ContentState.createFromBlockArray(blocks)
    const newEditorState = editorStateFromDefault(contentState)
    this.onReportContentChanged(newEditorState)
  }

  startWithBlank = () => {
    this.onReportContentChanged(editorStateFromDefault(null))
  }

  showRidePicker = () => {
    this.setState({showRidePicker: true})
  }

  hideRidePicker = () => {
    this.setState({showRidePicker: false})
  }

  render () {
    const {report, saveState, rides, photos, ridePhotos} = this.props
    const {editorState, showRidePicker, showMaxReachedDialog, saveFailureDialogDismissed} = this.state

    const allRidesLoaded = report && report.rides.every(rideId => rides.get(rideId)) // todo handle this

    if (!report) {
      return <LoadingPage title='Loading report...' message='Loading report...' />
    }

    // we have an un-initialized report, but we haven't loaded the rides yet
    if (report && !report.reportContent && !allRidesLoaded) {
      return <LoadingPage title='Loading report...' message='Loading ride data...' />
    }

    // we have a report where all the rides have been deleted
    if (report && report.rides.length === 0) {
      return <MessagePage title='Report error'>
        <div className={styles.errorIcon}>
          <Icon i='farthingWithFlat' size={200} />
        </div>
        <h1>No Rides Found</h1>
        <p>
          We were unable to load any rides for the report "{report.title}".
          They may have been deleted or removed.
        </p>
        <p>
          If you believe that you are seeing this message in error, please contact support.
        </p>
      </MessagePage>
    }

    if (!(editorState)) {
      return <div className={cx(styles.ShowPage, appStyles.base)}>
        <Navbar>
          <NavbarGroup>
            <li><NavbarTitle to='/ride_reports'>Ride Reports</NavbarTitle></li>
            <li>Choose your adventure</li>
          </NavbarGroup>
        </Navbar>
        <div className={styles.chooseYourAdventure}>
          <h1 className={appStyles.pageTitle}>Let's get started</h1>
          <p>You can start fresh with a blank ride report, or get rolling with an automatically generated draft that arranges
          your photos, ride stats, and route into a report that's ready for you
          to customize.</p>
          <div className={styles.columns}>
            <div className={styles.column}>
              <img src='/images/ride_reports/fresh-graphic.png' />
              <Button type='default' className={styles.button} onClick={this.startWithBlank}>Start Fresh</Button>
              <p className={styles.buttonExtra}>
                Start with a blank slate, and build your report just the way you want it
              </p>
            </div>
            <div className={styles.orCol}>
              <span className={styles.or}>or</span>
            </div>
            <div className={styles.column}>
              <img src='/images/ride_reports/draft-graphic.png' />
              <Button type='default' className={styles.button} onClick={this.autoPopulate}>Use Auto Draft</Button>
              <p className={styles.buttonExtra}>
                Let us add the photos, maps, and widgets; you just add your story
              </p>
            </div>
          </div>
        </div>
      </div>
    }

    // The Header component probably deserves a refactor as a redux connected
    // component soon, we're just jamming a lot of stuff into it.
    return (
      <div className={cx(styles.EditPage, appStyles.base)} onKeyDown={this.onKeyDown}>
        <Helmet title={`Editing '${report.title}'`} />
        <Header
          reportSlug={report.slug}
          saveState={saveState}
          onPublish={this.onStatusChange}
          onUndo={this.reportContentUndo}
          onRedo={this.reportContentRedo}
          hasUndo={editorState.getUndoStack().size > 0}
          hasRedo={editorState.getRedoStack().size > 0}
          toggleMetric={this.toggleMetric}
          metricUnits={report.metricUnits}
          buildDefaultReport={this.autoPopulate}
          publishStatus={report.publishStatus}
          onDelete={this.confirmDelete}
          repickRides={this.showRidePicker}
          showHints={this.props.showHints} />
        <MastheadEditor title={report.title} subtitle={report.subtitle} onChange={this.onTitleChange} />
        <ReportEditor
          editorState={editorState}
          report={report}
          rides={rides}
          photos={photos}
          ridePhotos={ridePhotos}
          onChange={this.onReportContentChanged} />
        <OnboardingContainer />
        {(!saveFailureDialogDismissed && saveState && saveState.failedSaves > 2) &&
          <SaveFailureDialog onRequestClose={this.dismissSaveFailureDialog} />}
        {showMaxReachedDialog && <MaxReachedDialog onRequestClose={this.dismissMaxReachedDialog} />}
        {showRidePicker && <RideRepicker
          selectedRides={rides}
          onConfirmSelection={this.onRidesChange}
          onDismiss={this.hideRidePicker} />}
      </div>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const slug = ownProps.params.slug
  const reportId = parseInt(slug, 10)
  const report = state.rideReports.reports.get(reportId)
  const rides = report ? state.trips.filter(keyIn(report.rides)) : Map()
  const ridePhotos = rides.reduce((a, b) => a.concat(b.get('photos')), List())
  const photos = report ? Immutable.fromJS(report.photos).concat(ridePhotos) : List()

  return {
    rides,
    photos,
    ridePhotos,
    report,
    reportId,
    saveState: state.editPage.saveState.get(reportId)
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  loadReport: (reportId) => dispatch(reportActions.loadReport(reportId)),
  updateReport: (report) => dispatch(reportActions.updateReport(report)),
  requestSave: (report, force) => dispatch(reportActions.requestSave(report, force)),
  deleteReport: (report) => dispatch(reportActions.deleteReport(report)),
  loadRide: (id) => dispatch(tripActions.loadTrip(id)),
  showHints: () => dispatch(userActions.setPreference('rr-onboarding-dismissed', false))
})

export const EditPageContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(EditPage)

// utilities

const LoadingPage = ({title, message}) => (
  <div className={cx(styles.ShowPage, appStyles.base)}>
    <Navbar>
      <NavbarGroup>
        <li><NavbarTitle to='/ride_reports'>Ride Reports</NavbarTitle></li>
        <li>{title}</li>
      </NavbarGroup>
    </Navbar>
    <div className={styles.loadingSpinner}>
      <CircleSpinner size={50} />
      <p>{message}</p>
    </div>
  </div>
)

const MessagePage = ({title, children}) => (
  <div className={cx(styles.ShowPage, appStyles.base)}>
    <Navbar>
      <NavbarGroup>
        <li><NavbarTitle to='/ride_reports'>Ride Reports</NavbarTitle></li>
        <li>{title}</li>
      </NavbarGroup>
    </Navbar>
    <div className={styles.messageContainer}>
      {children}
    </div>
  </div>
)
